import { type FC, memo, useCallback, useEffect, useMemo } from 'react';
import { useForm } from '@travelity/form/index';
import { AddProductForm, ProductRoute } from '@travelity/api/index';
import { Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useHelperContext } from '@contexts/helper-context';
import { useStepperContext } from '@components/stepper';
import {
    productRouteStepFormSchema,
    ProductRouteForm,
} from '@components/product-route-form';
import StepContent from './step-content';

export interface ProductRouteStepProps {}

const ProductRouteStep: FC<ProductRouteStepProps> = () => {
    const { t } = useTranslation();
    const { state, next, showErrors } =
        useStepperContext<Pick<AddProductForm, 'productInfo' | 'route'>>();
    const onSubmit = useCallback((data: ProductRoute) => {
        next({ ...state, route: data });
    }, []);
    const { Form, formState, watch } = useForm<ProductRoute>({
        onSubmit,
        schema: productRouteStepFormSchema,
        mode: 'onChange',
        validateInitially: true,
        defaultValues: state.route,
    });
    const errors = useMemo(() => {
        return Object.values(formState.errors)
            .map(error => error?.message)
            .filter(v => v) as string[];
    }, [formState]);

    // Handle helper texts
    const { setInfo } = useHelperContext();

    useEffect(() => {
        const callback = () => {
            setInfo(
                <>
                    <Box sx={{ fontWeight: 'bold' }}>
                        {t('Beginning of Route')}
                    </Box>
                    Any name/address that will describe the location where the
                    route starts as precisely as possible
                </>
            );
        };
        const el = document.querySelector('input[name=beginningLocation]');
        el?.addEventListener('focus', callback);
        return () => el?.removeEventListener('focus', callback);
    }, []);

    // TODO: Delete all setInfos
    // const repeatLocation = watch('repeatLocation');
    // useEffect(() => {
    //     const callback = () => {
    //         setInfo(
    //             <>
    //                 <Box sx={{ fontWeight: 'bold' }}>End of Route</Box>
    //                 Any name/address that will describe the location where the
    //                 route ends as precisely as possible. In case of a round
    //                 trip, Travelity will use the “Beginning of Route“ for this
    //                 value.
    //             </>
    //         );
    //     };
    //     const el = document.querySelector('input[name=endingLocation]');
    //     el?.addEventListener('focus', callback);
    //     return () => el?.removeEventListener('focus', callback);
    // }, [repeatLocation]);

    const stops = watch('stops');
    useEffect(() => {
        if (stops?.length) {
            setInfo(
                <>
                    <Box sx={{ fontWeight: 'bold' }}>{t('Stops')}</Box>
                    Stops are usually the main destinations of your tour, places
                    to be visited.
                    <br />
                    You can optionally define how long after the tour’s start
                    traveler(s) will reach the destination (Arrival Offset) and
                    how long they will stay in that destination (Stop Duration)
                </>
            );
        }
    }, [stops]);

    return (
        <StepContent title="Route" errors={errors}>
            <Form id="step">
                <ProductRouteForm showErrors={showErrors} isUpdate={false} />
            </Form>
        </StepContent>
    );
};

export default memo(ProductRouteStep);
